import React, { useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';
import ProjectSidebar from '../components/projects/ProjectSidebar';
import DocumentList from '../components/projects/DocumentList';
import { Box, Alert, Typography, Paper, LinearProgress, Drawer, IconButton, useTheme, useMediaQuery, Snackbar, Container } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ErrorPopup from '../components/ErrorPopup';
import {
  DOCUMENT_STATUSES,
  STORAGE,
  MESSAGES
} from '../config/constants';
import DocumentViewerModal from '../components/documents/DocumentViewerModal';
import { PERMISSIONS } from '../config/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { useNavigate, useLocation } from 'react-router-dom';
import UploadProgressIndicator from '../components/common/UploadProgressIndicator';
import { useFileUpload } from '../hooks/useFileUpload';
import { useTheme as useCustomTheme } from '../ThemeContext';

export default function Projects() {
  const { user, role } = useContext(AuthContext);
  const { theme: customTheme } = useCustomTheme();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [error, setError] = useState(null);
  const [errorPopup, setErrorPopup] = useState({ open: false, message: '', severity: 'error' });
  const [selectedDocument, setSelectedDocument] = useState(null);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [sortBy, setSortBy] = useState('name-asc');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [exportSnackbar, setExportSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Callback to clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedDocs([]);
  }, []);

  // Define fetchDocuments and showError first, wrapped in useCallback
  const fetchDocuments = useCallback(async (projectId) => {
    // No dependencies from component scope used directly here
    // supabase is stable, setDocuments/setError are stable setters
    try {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          id,
          name,
          created_at,
          storage_path,
          file_type,
          size,
          status,
          received_at,
          exported_at,
          exported_by,
          locked_by,
          profiles:locked_by (
            email,
            full_name
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include user info
      const transformedData = (data || []).map(doc => ({
        ...doc,
        lockedByUser: doc.profiles?.full_name || doc.profiles?.email || 'Onbekend',
        exported_by: doc.exporter?.email || '-'
      }));

      setDocuments(transformedData);
    } catch (error) {
      console.error('Fetch documents error:', error);
      setError(error.message);
    }
  }, [supabase, setDocuments, setError]); // Dependencies: stable setters and imported client

  const showError = useCallback((message, severity = 'error') => {
    setErrorPopup({ open: true, message, severity });
  }, [setErrorPopup]); // Dependency: stable setter

  // Now call the hook
  const { uploadProgress, uploadFiles } = useFileUpload(
    selectedProject?.id,
    fetchDocuments, // Now defined
    showError       // Now defined
  );

  // Get projectId from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('projectId');
    
    // Store the selected project ID for later use
    if (projectId) {
      localStorage.setItem('lastSelectedProjectId', projectId);
    }
  }, [location]);

  // Fetch projects and select the correct project
  useEffect(() => {
    let ignore = false;

    const fetchProjectsWrapper = async () => {
      try {
        let query = supabase
          .from('projects')
          .select('*');

        // Apply sorting based on sortBy value
        switch (sortBy) {
          case 'name-asc':
            query = query.order('name', { ascending: true });
            break;
          case 'name-desc':
            query = query.order('name', { ascending: false });
            break;
          case 'created-desc':
            query = query.order('created_at', { ascending: false });
            break;
          case 'created-asc':
            query = query.order('created_at', { ascending: true });
            break;
        }

        const { data, error } = await query;
        if (error) throw error;
        setProjects(data || []);
        
        // Check for project ID from URL or localStorage
        const params = new URLSearchParams(location.search);
        const projectId = params.get('projectId') || localStorage.getItem('lastSelectedProjectId');
        
        // Select project based on URL param or local storage, or default to first project
        if (projectId && data?.length > 0) {
          const projectToSelect = data.find(p => p.id === parseInt(projectId) || p.id === projectId);
          if (projectToSelect) {
            setSelectedProject(projectToSelect);
            return;
          }
        }
        
        // If no matching project found or no projectId, select first project by default
        if (data?.length > 0 && !selectedProject) {
          setSelectedProject(data[0]);
        }
      } catch (error) {
        setError(error.message);
      }
    };

    fetchProjectsWrapper();

    return () => {
      ignore = true;
    };
  }, [sortBy]);

  // Fetch documents when project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchDocuments(selectedProject.id); // Call the defined function
    }
  }, [selectedProject, fetchDocuments]); // Add fetchDocuments to dependency array

  const fetchProjects = async () => {
    try {
      let query = supabase
        .from('projects')
        .select('*');

      // Apply sorting based on sortBy value
      switch (sortBy) {
        case 'name-asc':
          query = query.order('name', { ascending: true });
          break;
        case 'name-desc':
          query = query.order('name', { ascending: false });
          break;
        case 'created-desc':
          query = query.order('created_at', { ascending: false });
          break;
        case 'created-asc':
          query = query.order('created_at', { ascending: true });
          break;
      }

      const { data, error } = await query;
      if (error) throw error;
      setProjects(data || []);
      
      // Check for project ID from URL or localStorage
      const params = new URLSearchParams(location.search);
      const projectId = params.get('projectId') || localStorage.getItem('lastSelectedProjectId');
      
      // Select project based on URL param or local storage, or default to first project
      if (projectId && data?.length > 0) {
        const projectToSelect = data.find(p => p.id === parseInt(projectId) || p.id === projectId);
        if (projectToSelect) {
          setSelectedProject(projectToSelect);
          return;
        }
      }
      
      // If no matching project found or no projectId, select first project by default
      if (data?.length > 0 && !selectedProject) {
        setSelectedProject(data[0]);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDownloadDoc = async (docId) => {
    try {
      const doc = documents.find(d => d.id === docId);
      if (!doc?.storage_path) throw new Error('No storage path found');

      const { data, error } = await supabase.storage
        .from('documents')
        .download(doc.storage_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      showError(error.message);
    }
  };

  // Fix: Use the hasPermission hook for checking document deletion permissions too
  const handleDeleteDoc = async (docId) => {
    // Fix: Use the correct permission string
    if (!hasPermission('delete_document')) {
      showError('Je hebt geen rechten om documenten te verwijderen');
      return;
    }

    if (!window.confirm('Weet je zeker dat je dit document wilt verwijderen?')) return;

    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;
      fetchDocuments(selectedProject.id);
    } catch (error) {
      showError(error.message);
    }
  };

  // Fix: Add function to handle bulk deletion
  const handleDeleteMultipleDocs = async (docIds) => {
    if (!hasPermission(PERMISSIONS.DOCUMENTS.DELETE)) {
      showError('Je hebt geen rechten om documenten te verwijderen');
      return;
    }

    if (!window.confirm(`Weet je zeker dat je ${docIds.length} documenten wilt verwijderen?`)) return;

    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .in('id', docIds); // Use .in for multiple IDs

      if (error) throw error;
      
      // Refetch documents and clear selection
      fetchDocuments(selectedProject.id);
      setSelectedDocs([]); // Clear the selection after deletion

    } catch (error) {
      showError('Fout bij het verwijderen van documenten: ' + error.message);
    }
  };

  // Fix: Add function for bulk exporting documents
  const handleExportMultipleDocs = async (docIds) => {
    const currentProjectId = selectedProject?.id;
    if (!currentProjectId) {
      showError('Kan documentenlijst niet vernieuwen: geen project geselecteerd.', 'warning');
    }

    if (!docIds || docIds.length === 0) {
       showError('Geen documenten geselecteerd om te exporteren.', 'warning');
       return;
    }

    let successCount = 0;
    let failCount = 0;
    const totalCount = docIds.length;

    setExportSnackbar({ open: true, message: `Start export voor ${totalCount} document(en)...`, severity: 'info' });

    for (let i = 0; i < totalCount; i++) {
      const docId = docIds[i];
      const docName = documents.find(d => d.id === docId)?.name || `ID: ${docId}`;
      const progressMessage = `Verwerken: ${docName} (${i + 1}/${totalCount})...`;
      setExportSnackbar({ open: true, message: progressMessage, severity: 'info' });

      try {
        // Roep de Supabase Edge Function aan voor DIT document
        // De functie is nu verantwoordelijk voor locken/status/unlocken
        console.log(`Invoking generate-sikb-xml for document ID: ${docId}`);
        const { data: exportData, error: functionError } = await supabase.functions.invoke('generate-sikb-xml', {
          body: { documentId: docId }, // Gebruik documentId
        });

        // Verwerk Response
        if (functionError || (exportData && !exportData.success)) {
          const errorMessage = functionError?.message || exportData?.error || 'Onbekende fout bij genereren XML.';
          console.error(`generate-sikb-xml function error for doc ${docName}:`, errorMessage, functionError, exportData);
          setExportSnackbar({ open: true, message: `Fout bij ${docName}: ${errorMessage}`, severity: 'error' });
          failCount++;
          // Geen frontend revert nodig, functie zou dit moeten doen
          continue; // Ga door naar het volgende document
        }

        // Succes voor dit document!
        console.log(`generate-sikb-xml success for doc ${docName}:`, exportData);
        setExportSnackbar({ open: true, message: `Succes voor ${docName}: ${exportData.fileName || 'XML gegenereerd'}`, severity: 'success' });
        successCount++;
        // Geen frontend unlock nodig, functie zou dit moeten doen

      } catch (loopError) {
        // Onverwachte frontend fout tijdens aanroep
        console.error(`Onverwachte fout tijdens aanroep voor document ${docName}:`, loopError);
        setExportSnackbar({ open: true, message: `Client-fout bij ${docName}: ${loopError.message}`, severity: 'error' });
        failCount++;
        // Geen database revert mogelijk vanuit frontend hier
      }
    } // Einde for loop

    // Afronding na de loop
    const finalMessage = `Export afgerond. Succes: ${successCount}/${totalCount}. Mislukt: ${failCount}/${totalCount}.`;
    setExportSnackbar({ 
        open: true, 
        message: finalMessage, 
        severity: failCount > 0 ? (successCount > 0 ? 'warning' : 'error') : 'success' 
    });

    // Documenten opnieuw ophalen om de (hopelijk bijgewerkte) statussen te tonen
    if (currentProjectId) {
        await fetchDocuments(currentProjectId);
    }
    setSelectedDocs([]); // Wis selectie na afloop

  };

  // Functie om geselecteerde documenten terug te zetten naar 'Beoordeeld'
  const handleRevertStatusBulk = async (docIds) => {
    if (!docIds || docIds.length === 0) {
      setExportSnackbar({ open: true, message: 'Geen documenten geselecteerd om terug te zetten.', severity: 'warning' });
      return;
    }

    // Optioneel: Vraag bevestiging
    if (!window.confirm(`Weet je zeker dat je ${docIds.length} document(en) wilt terugzetten naar de status "Beoordeeld"?`)) {
      return;
    }

    const totalCount = docIds.length;
    setExportSnackbar({ open: true, message: `Status van ${totalCount} document(en) terugzetten...`, severity: 'info' });

    try {
      // 1. Fetch storage paths for the documents to be reverted
      const { data: outputData, error: outputError } = await supabase
        .from('document_output')
        .select('id, storage_path') // Select ID for deletion later
        .in('document_id', docIds);

      if (outputError) {
        console.warn('Could not fetch storage paths for reverting documents:', outputError.message);
        // Proceed with status update anyway, but warn the user later? Or stop?
        // For now, we continue, but ideally handle this more gracefully.
      }

      const storagePathsToDelete = outputData?.map(o => o.storage_path).filter(Boolean) || [];
      const outputIdsToDelete = outputData?.map(o => o.id).filter(Boolean) || [];

      // 2. Update document status in 'documents' table
      const revertPayload = {
        status: DOCUMENT_STATUSES.REVIEWED,
        exported_at: null,
        exported_by: null,
        locked_by: null // Zet lock ook terug voor de zekerheid
      };

      const { error: updateError } = await supabase
        .from('documents')
        .update(revertPayload)
        .in('id', docIds);

      if (updateError) {
        // If status update fails, we don't proceed with deletion
        throw new Error(`Kon status niet terugzetten: ${updateError.message}`);
      }

      // 3. Delete XML files from storage (if any were found)
      let storageDeleteError = null;
      if (storagePathsToDelete.length > 0) {
        const { error: deleteFileError } = await supabase.storage
          .from('xml-output') // Use the correct bucket name
          .remove(storagePathsToDelete);

        if (deleteFileError) {
          console.error('Error deleting XML files from storage:', deleteFileError);
          storageDeleteError = deleteFileError; // Store error to report later
          // Continue processing other steps even if storage deletion fails?
        } else {
          console.log(`Successfully deleted ${storagePathsToDelete.length} XML file(s) from storage.`);
        }
      }

      // 4. Delete records from 'document_output' table (if any were found)
      let outputDeleteError = null;
      if (outputIdsToDelete.length > 0) {
        const { error: deleteOutputError } = await supabase
          .from('document_output')
          .delete()
          .in('id', outputIdsToDelete);

        if (deleteOutputError) {
          console.error('Error deleting records from document_output:', deleteOutputError);
          outputDeleteError = deleteOutputError; // Store error to report later
        } else {
          console.log(`Successfully deleted ${outputIdsToDelete.length} record(s) from document_output.`);
        }
      }

      // 5. Show final status message
      let finalMessage = `${totalCount} document(en) succesvol teruggezet naar 'Beoordeeld'.`;
      let finalSeverity = 'success';

      if (storageDeleteError || outputDeleteError) {
        finalMessage = `Status van ${totalCount} document(en) teruggezet, maar fout bij opruimen export data: ${storageDeleteError?.message || ''} ${outputDeleteError?.message || ''}`;
        finalSeverity = 'warning';
      } else if (storagePathsToDelete.length > 0) {
         finalMessage += ` ${storagePathsToDelete.length} geëxporteerde bestand(en) opgeruimd.`;
      }

      setExportSnackbar({ open: true, message: finalMessage, severity: finalSeverity });

      // 6. Refresh the list
      if (selectedProject?.id) {
        await fetchDocuments(selectedProject.id);
      }

      setSelectedDocs([]); // Wis selectie na succesvolle operatie

    } catch (error) {
      console.error('Revert status error:', error);
      setExportSnackbar({ open: true, message: `Fout bij terugzetten status: ${error.message}`, severity: 'error' });
    }
  };

  // Nieuwe functie om de Edge Function aan te roepen
  const handleReprocessDoc = async (docId) => {
    // Je kunt de admin check hier laten als snelle UI feedback,
    // maar de *echte* beveiliging zit in de Edge Function.
    if (role !== 'admin') {
      showError('Alleen beheerders kunnen documenten opnieuw verwerken.');
      return;
    }

    // Optioneel: Voeg een bevestigingsdialoog toe
    if (!window.confirm(`Weet je zeker dat je document ${docId} opnieuw wilt verwerken? Dit verwijdert alle bestaande extractiegegevens.`)) {
        return;
    }

    console.log(`Invoking edge function 'reprocess-document' for document ID: ${docId}`);

    try {
      // Roep de Edge Function aan
      const { data, error: functionError } = await supabase.functions.invoke('reprocess-document', {
        body: { documentId: docId },
      });

      if (functionError) {
        // Probeer een specifiekere foutmelding uit de function response te halen
        const message = functionError.context?.message || functionError.message || 'Onbekende fout';
        console.error('Edge function error:', functionError);
        throw new Error(`Fout bij het aanroepen van de herverwerkingsfunctie: ${message}`);
      }

      // Function succesvol aangeroepen
      console.log('Edge function response:', data);
      showError(data.message || `Document ${docId} succesvol opnieuw ingediend voor verwerking.`, 'success');

      // Documenten opnieuw ophalen om de statuswijziging weer te geven
      if (selectedProject) {
        fetchDocuments(selectedProject.id);
      }

    } catch (error) {
      console.error('Reprocessing failed:', error);
      // Geef de foutmelding weer die uit de try/catch of de functionError komt
      showError(`Fout bij het herverwerken van document ${docId}: ${error.message}`);
    }
  };

  const handleUploadFiles = async (files) => {
    // Fix: Use the correct permission string
    if (!hasPermission('upload_document')) {
      showError('Je hebt geen rechten om documenten te uploaden');
      return;
    }

    if (!selectedProject) {
      showError('Selecteer eerst een project voordat je documenten upload');
      return;
    }

    if (!files.length) { 
      showError(MESSAGES.ERRORS.NO_FILES);
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(file => !STORAGE.ALLOWED_TYPES.includes(file.type));
    if (invalidFiles.length > 0) {
      showError(MESSAGES.ERRORS.FILE_TYPE);
      return;
    }

    // Check file sizes
    const oversizedFiles = files.filter(file => file.size > STORAGE.MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      showError(MESSAGES.ERRORS.FILE_SIZE);
      return;
    }

    // Initialize upload progress state
    setUploadProgress({
      visible: true,
      totalFiles: files.length,
      completedFiles: 0,
      currentFileProgress: 0,
      currentFileName: files[0]?.name || '',
      error: false,
      errorMessage: ''
    });

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Update current file info
        setUploadProgress(prev => ({
          ...prev,
          currentFileName: file.name,
          currentFileProgress: 0
        }));
        
        // Create sanitized filename for storage while keeping original name
        const timestamp = Date.now();
        const originalName = file.name;
        const sanitizedName = sanitizeFilename(file.name);
        const storageName = `${timestamp}-${sanitizedName}`;
        const filePath = `${selectedProject.id}/${storageName}`;

        // Upload file with progress tracking
        const { error: uploadError } = await new Promise((resolve) => {
          const uploadTask = supabase.storage
            .from(STORAGE.BUCKET_NAME)
            .upload(filePath, file, {
              contentType: file.type,
              cacheControl: '3600'
            });

          // Simulate progress updates since Supabase doesn't provide upload progress
          const totalSize = file.size;
          let simulatedLoaded = 0;
          const progressInterval = setInterval(() => {
            if (simulatedLoaded < totalSize) {
              // Simulate progress as a percentage of file size
              simulatedLoaded += totalSize / 20; // 5% increments
              if (simulatedLoaded > totalSize) simulatedLoaded = totalSize;
              
              const percent = Math.min(95, Math.round((simulatedLoaded / totalSize) * 100));
              setUploadProgress(prev => ({
                ...prev,
                currentFileProgress: percent
              }));
            }
          }, 200);

          uploadTask.then(result => {
            clearInterval(progressInterval);
            resolve(result);
          });
        });

        if (uploadError) throw uploadError;

        // Set progress to 100% when file is completely uploaded
        setUploadProgress(prev => ({
          ...prev,
          currentFileProgress: 100
        }));

        // Create document record with original filename
        const { error: dbError } = await supabase
          .from('documents')
          .insert([{
            project_id: selectedProject.id,
            name: originalName, // Store original filename
            storage_path: filePath,
            file_type: file.type,
            size: file.size,
            status: DOCUMENT_STATUSES.INTAKE,
            uploaded_by: user.id,
            received_at: new Date().toISOString()
          }]);

        if (dbError) throw dbError;

        // Update completed files count
        setUploadProgress(prev => ({
          ...prev,
          completedFiles: i + 1
        }));
      }

      // Final update - all files completed
      setUploadProgress(prev => ({
        ...prev,
        currentFileName: 'Afgerond',
        currentFileProgress: 100
      }));

      await fetchDocuments(selectedProject.id);
      
      // Hide progress indicator after a short delay
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, visible: false }));
      }, 2000);
    } catch (error) {
      console.error('Upload error:', error);
      showError(MESSAGES.ERRORS.UPLOAD_FAILED);
      
      // Show error in progress indicator
      setUploadProgress(prev => ({
        ...prev,
        error: true,
        errorMessage: MESSAGES.ERRORS.UPLOAD_FAILED
      }));
      
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, visible: false }));
      }, 5000);
    }
  };

  // Add function to update document status
  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      const { error } = await supabase
        .from('documents')
        .update({ 
          status: newStatus,
          ...(newStatus === 'exported' ? { exported_at: new Date().toISOString(), exported_by: user.id } : {})
        })
        .eq('id', docId);

      if (error) throw error;
      await fetchDocuments(selectedProject.id);
    } catch (error) {
      showError('Error updating document status: ' + error.message);
    }
  };

  const canEditDocument = (document) => {
    return !document.locked_by || document.locked_by === user.id;
  };

  const handleDocumentSelect = (document) => {
    if (!canEditDocument(document)) {
      showError('Dit document is vergrendeld door een andere gebruiker');
      return;
    }
    setSelectedDocument(document);
  };

  const handleUpdateDocuments = useCallback((updatedDocs) => {
    setDocuments(updatedDocs);
  }, []);

  const handleUnlockDoc = async (docId) => {
    try {
      const doc = documents.find(d => d.id === docId);
      if (!doc) throw new Error('Document niet gevonden');

      if (!hasPermission(PERMISSIONS.DOCUMENTS.UNLOCK)) {
        showError('Je hebt geen rechten om documenten vrij te geven');
        return;
      }

      const { error } = await supabase
        .from('documents')
        .update({ locked_by: null })
        .eq('id', docId);

      if (error) throw error;

      // Als dit de huidige gebruiker is die het document bekijkt, sluit het dan
      if (selectedDocument?.id === docId) {
        setSelectedDocument(null);
      }

    } catch (error) {
      showError('Error unlocking document: ' + error.message);
    }
  };

  // Handler to toggle sidebar
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handler to toggle sidebar collapse
  const handleSidebarToggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const sidebarComponent = (
    <ProjectSidebar
      projects={projects}
      selectedProject={selectedProject}
      onProjectSelect={(project) => {
        setSelectedProject(project);
        handleClearSelection(); // Clear selection on project change
        if (isMobile) { // Close drawer on mobile after selection
          setSidebarOpen(false);
        }
      }}
      sortBy={sortBy}
      onSortChange={setSortBy}
      isCollapsed={sidebarCollapsed}
      onToggleCollapse={handleSidebarToggleCollapse}
      // Assuming canCreateProject permission logic exists or can be added
      // canCreateProject={hasPermission(PERMISSIONS.CREATE_PROJECT)} 
    />
  );

  const sidebarWidth = sidebarCollapsed ? 64 : 280;

  const contentComponent = (
    <Box sx={{ 
      flexGrow: 1, 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'background.default'
    }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText',
          py: 3,
          px: { xs: 2, md: 3 },
          borderBottom: 1,
          borderColor: 'divider',
          borderRadius: 0
        }}
      >
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleSidebarToggle}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              mr: 2,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, display: 'inline-block' }}>
          {selectedProject?.name || 'Geen project geselecteerd'}
        </Typography>
      </Paper>

      {/* Content Section */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        p: { xs: 2, md: 3 }
      }}>
        <DocumentList
          documents={documents.map(doc => ({
            ...doc,
            isLocked: !!doc.locked_by,
            isLockedByMe: doc.locked_by === user.id,
            canEdit: canEditDocument(doc)
          }))}
          selectedTab={selectedTab}
          onTabChange={(e, newValue) => {
            setSelectedTab(newValue);
            handleClearSelection(); // Clear selection on tab change
          }}
          selectedDocs={selectedDocs}
          onSelectDoc={(docId, isSelected) => {
            setSelectedDocs(prev => 
              isSelected 
                ? [...prev, docId]
                : prev.filter(id => id !== docId)
            );
          }}
          onDeleteDoc={handleDeleteDoc}
          onDownloadDoc={handleDownloadDoc}
          onUploadFiles={uploadFiles} // Corrected: pass uploadFiles from hook
          onUpdateStatus={handleUpdateStatus}
          onDocumentSelect={handleDocumentSelect}
          onUpdateDocuments={handleUpdateDocuments}
          onUnlockDoc={handleUnlockDoc}
          onDeleteMultipleDocs={handleDeleteMultipleDocs}
          onExportMultipleDocs={handleExportMultipleDocs}
          onClearSelection={handleClearSelection}
          onReprocessDoc={handleReprocessDoc}
          userRole={role}
          onShowSnackbar={setExportSnackbar}
          onRevertStatus={handleRevertStatusBulk}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        height: 'calc(100vh - 64px)',
        bgcolor: 'background.default'
      }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={sidebarOpen}
            onClose={handleSidebarToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 280,
                top: '64px',
                height: 'calc(100% - 64px)',
                borderRadius: 0
              }, 
            }}
          >
            {sidebarComponent}
          </Drawer>
        ) : (
          <Box sx={{ 
            width: sidebarWidth, 
            flexShrink: 0, 
            height: '100%',
            borderRight: 1,
            borderColor: 'divider',
            transition: 'width 0.3s ease'
          }}>
             {sidebarComponent}
          </Box>
        )}
        {/* Main Content Area */}
        {contentComponent}
      </Box>

      {/* Upload Progress Indicator */}
      <UploadProgressIndicator uploadProgress={uploadProgress} />
      
      <DocumentViewerModal
        open={!!selectedDocument}
        onClose={() => setSelectedDocument(null)}
        document={selectedDocument}
      />
      <ErrorPopup
        open={errorPopup.open}
        message={errorPopup.message}
        severity={errorPopup.severity}
        onClose={() => setErrorPopup({ open: false, message: '', severity: 'error' })}
      />
      {/* Snackbar voor export feedback */}
      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setExportSnackbar({ ...exportSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setExportSnackbar({ ...exportSnackbar, open: false })} 
          severity={exportSnackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {exportSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
